import { createContext, useContext } from 'react';

// Deployment modes
export type DeploymentMode = 'saas' | 'on-premise';

// Feature configuration interface
export interface FeatureConfig {
  mode: DeploymentMode;
  multiTenant: boolean;
  licensing: boolean;
  roleAssumption: boolean;
  platformAdmin: boolean;
  dataUpload: boolean;
}

// SaaS preset - full features
export const SAAS_CONFIG: FeatureConfig = {
  mode: 'saas',
  multiTenant: true,
  licensing: true,
  roleAssumption: true,
  platformAdmin: true,
  dataUpload: true,
};

// On-premise preset - simplified single-org deployment
export const ON_PREM_CONFIG: FeatureConfig = {
  mode: 'on-premise',
  multiTenant: false,
  licensing: false,
  roleAssumption: false,
  platformAdmin: false,
  dataUpload: true,
};

// Resolve config from environment variable at build time
export function getFeatureConfig(): FeatureConfig {
  const mode = (import.meta.env.VITE_DEPLOYMENT_MODE as DeploymentMode) || 'saas';
  return mode === 'on-premise' ? ON_PREM_CONFIG : SAAS_CONFIG;
}

// On-premise role mapping: simplified roles → internal roles
export const ON_PREM_ROLE_MAP: Record<string, string> = {
  admin: 'cra_admin',
  analyst: 'cra_analyst',
  viewer: 'bank_user',
};

// Reverse mapping for display purposes
export const ON_PREM_ROLE_DISPLAY: Record<string, string> = {
  cra_admin: 'Admin',
  cra_analyst: 'Analyst',
  bank_user: 'Viewer',
};

// Available roles per mode
export const AVAILABLE_ROLES = {
  saas: ['platform_admin', 'cra_admin', 'cra_analyst', 'rating_committee', 'bank_admin', 'bank_user'] as const,
  'on-premise': ['admin', 'analyst', 'viewer'] as const,
};

// Map an on-prem role to internal role (passthrough for SaaS)
export function mapRole(role: string, mode: DeploymentMode): string {
  if (mode === 'on-premise' && ON_PREM_ROLE_MAP[role]) {
    return ON_PREM_ROLE_MAP[role];
  }
  return role;
}

// Get display name for a role
export function getRoleDisplayName(role: string, mode: DeploymentMode): string {
  if (mode === 'on-premise' && ON_PREM_ROLE_DISPLAY[role]) {
    return ON_PREM_ROLE_DISPLAY[role];
  }
  return role;
}

// Check if current mode is on-premise
export function isOnPremise(): boolean {
  return getFeatureConfig().mode === 'on-premise';
}

// React Context for feature configuration
const FeatureConfigContext = createContext<FeatureConfig>(getFeatureConfig());

export const FeatureConfigProvider = FeatureConfigContext.Provider;

export function useFeatureConfig(): FeatureConfig {
  return useContext(FeatureConfigContext);
}

// Check if a specific feature is enabled
export function useFeature(feature: keyof FeatureConfig): boolean {
  const config = useFeatureConfig();
  return Boolean(config[feature]);
}
